import http from '../../../services/api'
import type { APIPaginatedResponse, APIResponse, Filters, Pagination } from '../../../services/types'
import { getRequestParamString } from '../../../services/utils/utils'

const apiPath = import.meta.env.VITE_API_PATH

export type SupplierApi = {
  uuid: string
  name: string
  company_name?: string | null
  company_phone?: string | null
  company_email?: string | null
  phone: string
  email?: string | null
  tax_number?: string | null
  address: string
  currency_code?: string | null
  payment_terms_days?: number | null
  opening_balance?: number | null
  notes?: string | null
  is_active: boolean
  business_id?: number
  created_at?: string
  updated_at?: string
}

export type CreateSupplierInput = {
  name: string
  company_name?: string | null
  company_phone?: string | null
  company_email?: string | null
  phone: string
  email?: string | null
  tax_number?: string | null
  address: string
  currency_code?: string | null
  payment_terms_days?: number | null
  opening_balance?: number | null
  notes?: string | null
  is_active?: boolean
}

export type UpdateSupplierInput = CreateSupplierInput & {
  uuid: string
}

const normalizeStringOrNull = (v: unknown): string | null => {
  const s = typeof v === 'string' ? v.trim() : ''
  return s.length ? s : null
}

async function getSuppliers(filters?: Partial<Filters>, pagination?: Partial<Pagination>) {
  const paramString = getRequestParamString(filters, pagination)
  return await http.get<APIPaginatedResponse<SupplierApi>>(apiPath + 'procurement/suppliers' + paramString)
}

async function createSupplier(input: CreateSupplierInput) {
  // Keep payload explicit to avoid accidentally sending extra reactive fields.
  const payload = {
    name: input.name.trim(),
    company_name: normalizeStringOrNull(input.company_name),
    company_phone: normalizeStringOrNull(input.company_phone),
    company_email: normalizeStringOrNull(input.company_email),
    phone: input.phone.trim(),
    email: normalizeStringOrNull(input.email),
    tax_number: normalizeStringOrNull(input.tax_number),
    address: input.address.trim(),
    currency_code: normalizeStringOrNull(input.currency_code),
    payment_terms_days: input.payment_terms_days ?? null,
    opening_balance: input.opening_balance ?? null,
    notes: normalizeStringOrNull(input.notes),
    is_active: input.is_active ?? true,
  }
  return await http.post<APIResponse<SupplierApi>>(apiPath + 'procurement/suppliers', payload)
}

async function updateSupplier(input: UpdateSupplierInput) {
  const payload = {
    name: input.name.trim(),
    company_name: normalizeStringOrNull(input.company_name),
    company_phone: normalizeStringOrNull(input.company_phone),
    company_email: normalizeStringOrNull(input.company_email),
    phone: input.phone.trim(),
    email: normalizeStringOrNull(input.email),
    tax_number: normalizeStringOrNull(input.tax_number),
    address: input.address.trim(),
    currency_code: normalizeStringOrNull(input.currency_code),
    payment_terms_days: input.payment_terms_days ?? null,
    opening_balance: input.opening_balance ?? null,
    notes: normalizeStringOrNull(input.notes),
    is_active: input.is_active ?? true,
  }
  return await http.put<APIResponse<SupplierApi>>(`${apiPath}procurement/suppliers/${input.uuid}`, payload)
}

async function deactivateSupplier(uuid: string) {
  // Ticket specifies PUT for deactivate.
  return await http.put<APIResponse<SupplierApi>>(`${apiPath}procurement/suppliers/${uuid}/deactivate`, {})
}

const suppliersService = {
  getSuppliers,
  createSupplier,
  updateSupplier,
  deactivateSupplier,
}

export default suppliersService
